import React from 'react';
import * as R from 'ramda';
import { getSelectedGroupId, hasActiveFilters } from 'ducks/products';
import { usePlannerConfig } from 'contexts/plannerConfig';
import IconButtonSizeEnum from '@inter-ikea-kompis/component-icon-button/lib/enums/IconButtonSizeEnum';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Button from 'ui/Button/Button';
import { ButtonTypeEnum } from 'ui/Button/Button';
import useIsRtl from 'hooks/useIsRtl';
import { useIntl } from 'react-intl';
import FiltersIcon from '@ingka/ssr-icon/paths/filters';

const BaseFilterWrapper = styled.div`
  &[data-selected='true'] {
    &::after {
      content: '';
      position: absolute;
      height: 10px;
      width: 10px;
      background-color: ${({ theme }) => theme.colors.semantic.positive};
      border-radius: 50%;
    }
    &[data-rtl='true'] {
      transform: scaleX(-1);
      &::after {
        margin-right: -8px;
      }
    }
  }
`;

const FilterWrapper = styled(BaseFilterWrapper)`
  &[data-selected='true'] {
    &::after {
      top: ${({ theme }) => theme.space.xs};
      right: ${({ theme }) => theme.space.xs};
    }
  }
`;

const FilterWrapperInDrawer = styled(BaseFilterWrapper)`
  &[data-selected='true'] {
    &::after {
      top: ${({ theme }) => theme.space.xl};
      right: ${({ theme }) => theme.space.l};
    }
  }
`;

type Props = {
  onClick: () => void;
  hidden?: boolean;
  inDrawer?: boolean;
};

const FilterButton = ({ onClick, hidden = false, inDrawer = false }: Props) => {
  const selectedGroupId = useSelector(getSelectedGroupId);
  const isRtl = useIsRtl();
  const { formatMessage } = useIntl();

  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const selectedGroup = R.find(R.propEq('id', selectedGroupId), productGroups);
  const groupHasFilters = selectedGroup?.mainMenu.filters?.length !== 0;

  const filtersApplied = useSelector(hasActiveFilters);
  const activeFilter = groupHasFilters && filtersApplied(selectedGroup);

  const Wrapper = inDrawer ? FilterWrapperInDrawer : FilterWrapper;

  const hideFilter = hidden ? true : !groupHasFilters;

  return (
    <Wrapper data-rtl={isRtl} data-selected={activeFilter}>
      <Button
        iconOnly
        size={IconButtonSizeEnum.medium}
        type={ButtonTypeEnum.tertiary}
        ssrIcon={FiltersIcon}
        onClick={onClick}
        hidden={hideFilter}
        aria-label={formatMessage({ id: 'storageone.filterToggleOptions' })}
      />
    </Wrapper>
  );
};

export default FilterButton;
